import { memo, useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Bell, Edit2, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button, Label, Input } from '@caruso/ui';
import { useQuery } from '@tanstack/react-query';
import { usersService } from '@/services/users.service';

type NotificationTargetType = 'USER' | 'GROUP' | 'ALL';
type NotificationType = 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS' | 'TASK' | 'FLOW' | 'BOARD' | 'CLIENT' | 'ORDER' | 'RESERVATION';

interface NotificationNodeData {
  targetType?: NotificationTargetType;
  targetId?: string;
  notificationType?: NotificationType;
  title?: string;
  message?: string;
  link?: string;
  targetName?: string;
}

const targetTypeLabels: Record<NotificationTargetType, string> = {
  USER: 'Usuario Especifico',
  GROUP: 'Grupo de Usuarios',
  ALL: 'Todos os Usuarios',
};

const notificationTypeLabels: Record<NotificationType, { label: string; color: string }> = {
  INFO: { label: 'Informativo', color: 'bg-blue-400' },
  WARNING: { label: 'Aviso', color: 'bg-yellow-400' },
  ERROR: { label: 'Erro', color: 'bg-red-400' },
  SUCCESS: { label: 'Sucesso', color: 'bg-green-400' },
  TASK: { label: 'Tarefa', color: 'bg-purple-400' },
  FLOW: { label: 'Fluxo', color: 'bg-cyan-400' },
  BOARD: { label: 'Quadro', color: 'bg-indigo-400' },
  CLIENT: { label: 'Cliente', color: 'bg-emerald-400' },
  ORDER: { label: 'Pedido', color: 'bg-orange-400' },
  RESERVATION: { label: 'Reserva', color: 'bg-pink-400' },
};

export const NotificationNode = memo(({ data, id }: NodeProps<NotificationNodeData>) => {
  const { deleteElements, setNodes } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);
  const [targetType, setTargetType] = useState<NotificationTargetType>(data.targetType || 'ALL');
  const [targetId, setTargetId] = useState<string>(data.targetId || '');
  const [notificationType, setNotificationType] = useState<NotificationType>(data.notificationType || 'INFO');
  const [title, setTitle] = useState<string>(data.title || '');
  const [message, setMessage] = useState<string>(data.message || '');
  const [link, setLink] = useState<string>(data.link || '');

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersService.getAll(),
    enabled: isEditing && targetType === 'USER',
  });

  const { data: groupsData } = useQuery({
    queryKey: ['user-groups'],
    queryFn: () => fetch('/api/user-groups').then(res => res.json()),
    enabled: isEditing && targetType === 'GROUP',
  });

  const handleDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };

  const handleSave = () => {
    let targetName = '';
    if (targetType === 'USER') {
      const user = usersData?.find((u) => u.id === targetId);
      targetName = user?.name || '';
    } else if (targetType === 'GROUP') {
      const group = groupsData?.find((g: any) => g.id === targetId);
      targetName = group?.name || '';
    } else {
      targetName = 'Todos';
    }

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              targetType,
              targetId: targetType === 'ALL' ? undefined : targetId,
              notificationType,
              title,
              message,
              link: link || undefined,
              targetName,
            },
          };
        }
        return node;
      })
    );
    setIsEditing(false);
  };

  const isConfigured = data.title && data.message;

  return (
    <>
      <div className="bg-amber-500/10 border-2 border-amber-500 rounded-lg p-4 min-w-[280px] shadow-lg">
        <Handle type="target" position={Position.Top} className="!bg-amber-500" />

        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
              <Bell className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-amber-600">Enviar Notificacao</div>
              <div className="text-xs text-gray-500">Notifica usuarios do sistema</div>
            </div>
          </div>

          <div className="flex gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 hover:bg-amber-100 rounded transition-colors"
              title="Editar"
            >
              <Edit2 className="w-4 h-4 text-amber-600" />
            </button>
            <button
              onClick={handleDelete}
              className="p-1.5 hover:bg-red-100 rounded transition-colors"
              title="Excluir"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          {isConfigured ? (
            <>
              <div className="text-xs text-gray-600">
                <span className="font-medium">Para:</span> {data.targetName || targetTypeLabels[data.targetType || 'ALL']}
              </div>
              <div className="text-xs text-gray-600">
                <span className="font-medium">Tipo:</span>
                <span className={`ml-1 px-1.5 py-0.5 rounded text-white text-[10px] ${notificationTypeLabels[data.notificationType || 'INFO'].color}`}>
                  {notificationTypeLabels[data.notificationType || 'INFO'].label}
                </span>
              </div>
              <div className="text-xs text-gray-600 truncate">
                <span className="font-medium">Titulo:</span> {data.title}
              </div>
            </>
          ) : (
            <div className="text-xs text-amber-600 italic">
              Clique para configurar
            </div>
          )}
        </div>

        <Handle type="source" position={Position.Bottom} className="!bg-amber-500" />
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-brand-ink border-brand-gold/20 text-brand-ivory max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-brand-gold flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Configurar Notificacao
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/60">
              Configure quem recebera a notificacao e seu conteudo
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-brand-ivory/80">
                Enviar para <span className="text-red-400">*</span>
              </Label>
              <select
                value={targetType}
                onChange={(e) => {
                  setTargetType(e.target.value as NotificationTargetType);
                  setTargetId('');
                }}
                className="w-full bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory mt-1 rounded px-3 py-2 h-10"
              >
                <option value="ALL">Todos os Usuarios</option>
                <option value="USER">Usuario Especifico</option>
                <option value="GROUP">Grupo de Usuarios</option>
              </select>
            </div>

            {targetType === 'USER' && (
              <div>
                <Label className="text-brand-ivory/80">
                  Usuario <span className="text-red-400">*</span>
                </Label>
                <select
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                  className="w-full bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory mt-1 rounded px-3 py-2 h-10"
                >
                  <option value="">Selecione um usuario</option>
                  {usersData?.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {targetType === 'GROUP' && (
              <div>
                <Label className="text-brand-ivory/80">
                  Grupo <span className="text-red-400">*</span>
                </Label>
                <select
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                  className="w-full bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory mt-1 rounded px-3 py-2 h-10"
                >
                  <option value="">Selecione um grupo</option>
                  {groupsData?.map((group: any) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <Label className="text-brand-ivory/80">
                Tipo da Notificacao
              </Label>
              <select
                value={notificationType}
                onChange={(e) => setNotificationType(e.target.value as NotificationType)}
                className="w-full bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory mt-1 rounded px-3 py-2 h-10"
              >
                {Object.entries(notificationTypeLabels).map(([key, config]) => (
                  <option key={key} value={key}>
                    {config.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-brand-ivory/80">
                Titulo <span className="text-red-400">*</span>
              </Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Cliente chegou na etapa Cancelado"
                className="bg-brand-ink/50 border-brand-gold/20 text-brand-ivory mt-1"
              />
              <p className="text-xs text-brand-ivory/50 mt-1">
                Use {'{{client.name}}'} para inserir o nome do cliente
              </p>
            </div>

            <div>
              <Label className="text-brand-ivory/80">
                Mensagem <span className="text-red-400">*</span>
              </Label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ex: O cliente {{client.name}} foi movido para cancelado no quadro..."
                rows={3}
                className="w-full bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory mt-1 rounded px-3 py-2 resize-none"
              />
              <p className="text-xs text-brand-ivory/50 mt-1">
                Variaveis disponiveis: {'{{client.name}}'}, {'{{client.phone}}'}, {'{{flow.name}}'}, {'{{step.name}}'}
              </p>
            </div>

            <div>
              <Label className="text-brand-ivory/80">
                Link (opcional)
              </Label>
              <Input
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="Ex: /app/clientes/{{client.id}}"
                className="bg-brand-ink/50 border-brand-gold/20 text-brand-ivory mt-1"
              />
              <p className="text-xs text-brand-ivory/50 mt-1">
                Link para onde o usuario sera direcionado ao clicar
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setIsEditing(false)}
              className="border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10"
            >
              Cancelar
            </Button>
            <Button
              variant="premium"
              onClick={handleSave}
              disabled={!title || !message || (targetType !== 'ALL' && !targetId)}
              className="bg-amber-500 hover:bg-amber-600"
            >
              Salvar Configuracao
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

NotificationNode.displayName = 'NotificationNode';
